import { grey } from "@mui/material/colors";
import {
  AttachmentsIcon,
  Box,
  DeleteIcon,
  Divider,
  DragDrop,
  FormControlLabel,
  GKButton,
  GKEditor,
  GKRadio,
  GKSearchSelect,
  GKTextArea,
  GKTextField,
  Grid2,
  IconButton,
  PhoneInput,
  PlusIcon,
  RadioGroup,
  Show,
  Typography,
} from "components";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { AdvisorCompliaceInterface } from "interfaces/SettingsInterface";
import { ComplianceGrievanceInterface } from "interfaces/WebsiteInterface";
import { orderBy, split } from "lodash";
import { financialYearFilter } from "Pages/RevenueManagement/utils";
import { useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useAppSelector } from "store/store";
import axiosInstance from "utils/axiosInstance";
import {
  handleCreateFormData,
  handleReturnNaturalRowsForString,
} from "utils/formHelpers";
import AuditFilesUpload from "./AuditFilesUpload";

const ComplianceSettings = () => {
  const userData = useAppSelector((state) => state?.user?.userData);

  const [showFilesModal, setShowFilesModal] = useState(false);

  const [uploadError, setUploadError] = useState(null);

  const [attachmentsIndex, setAttachmentsIndex] = useState(0);

  const [hasError, setHasError] = useState(false);

  const { mutate } = useMutation({
    mutationFn: (data: any) =>
      axiosInstance.patch(`/compliance-settings/${userData?.companyId}/`, data),
  });

  const form = useForm<
    AdvisorCompliaceInterface & ComplianceGrievanceInterface
  >({
    defaultValues: {
      complianceAudit: [
        {
          report1: [],
          report2: [],
          year: "",
        },
      ],
      flowType: "",
      charter: [],
      disclosure: "",
      sebiRegisteredSupervisoryBody: "",
      supervisoryBodyMembershipId: "",
      complianceOfficerName: "",
      grievanceOfficerName: "",
      complianceRedressalEmail: "",
      grievanceRedressalEmail: "",
      compliancePhoneNumber: "",
      grievancePhoneNumber: "",
      complianceCountryCode: "+91",
      grievanceCountryCode: "+91",
      refundPolicy: "",
      termsConditions: "",
      pricingPolicy: "",
      company: null,
    },
  });

  useFetchQuery({
    key: ["GET_ALL_COMPLIANCE_DATA"],
    route: "/compliance-settings/",
    success: (response: any) => {
      form.reset({
        complianceAudit:
          response?.data?.data?.complianceAudit?.length !== 0
            ? response?.data?.data?.complianceAudit?.map((d: any) => {
                return {
                  year: d?.year,
                  report1: [
                    {
                      name:
                        split(split(d?.report2 || "", "?")[0], "/")[
                          split(split(d?.report2 || "", "?")[0], "/").length - 1
                        ] || "",
                      file: d?.report2,
                      size: 0,
                    },
                  ],
                  report2: [
                    {
                      name:
                        split(split(d?.report1 || "", "?")[0], "/")[
                          split(split(d?.report1 || "", "?")[0], "/").length - 1
                        ] || "",
                      file: d?.report1,
                      size: 0,
                    },
                  ],
                };
              })
            : [
                {
                  report1: [],
                  report2: [],
                  year: "",
                },
              ],
        flowType: response?.data?.data?.flowType || "",
        charter: response?.data?.data?.charter
          ? [
              {
                name:
                  split(
                    split(response?.data?.data?.charter || "", "?")[0],
                    "/",
                  )[
                    split(
                      split(response?.data?.data?.charter || "", "?")[0],
                      "/",
                    ).length - 1
                  ] || "",
                file: response?.data?.data?.charter,
                size: 0,
              },
            ]
          : [],
        disclosure: response?.data?.data?.disclosure || "",
        sebiRegisteredSupervisoryBody:
          response?.data?.data?.sebiRegisteredSupervisoryBody,
        supervisoryBodyMembershipId:
          response?.data?.data?.supervisoryBodyMembershipId,
        complianceOfficerName: response?.data?.data?.complianceOfficerName,
        grievanceOfficerName: response?.data?.data?.grievanceOfficerName,
        complianceRedressalEmail:
          response?.data?.data?.complianceRedressalEmail,
        grievanceRedressalEmail: response?.data?.data?.grievanceRedressalEmail,
        compliancePhoneNumber: response?.data?.data?.compliancePhoneNumber,
        grievancePhoneNumber: response?.data?.data?.grievancePhoneNumber,
        complianceCountryCode: response?.data?.data?.complianceCountryCode,
        grievanceCountryCode: response?.data?.data?.grievanceCountryCode,
        company: response?.data?.data?.company,
        refundPolicy: response?.data?.data?.refundPolicy,
        termsConditions: response?.data?.data?.termsConditions,
        pricingPolicy: response?.data?.data?.pricingPolicy,
      });
    },
  });

  form.watch();

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit((values) => {
          mutate(
            handleCreateFormData({
              values: values,
              dirtyFields: form.formState.dirtyFields,
            }),
          );
        })}
      >
        <Grid2 container spacing={3}>
          <Grid2 size={3}>
            <Typography
              fontSize={16}
              fontWeight={500}
              color={"var(--neutral-500)"}
            >
              License Type
            </Typography>
          </Grid2>
          <Grid2
            container
            size={9}
            spacing={2}
            display={"flex"}
            alignItems={"center"}
          >
            <Controller
              control={form.control}
              name="flowType"
              render={({ field }) => (
                <RadioGroup
                  {...field}
                  row
                  onChange={(e) => field.onChange(e.target.value)}
                >
                  <FormControlLabel
                    label="RIA (Registered Investment Advisor)"
                    value="ria"
                    control={<GKRadio />}
                  />
                  <FormControlLabel
                    label="RA (Research Analyst)"
                    value="ra"
                    control={<GKRadio />}
                  />
                </RadioGroup>
              )}
            />
          </Grid2>
          <Grid2 size={12}>
            <Divider />
          </Grid2>
          <Grid2 size={3}>
            <Typography
              fontSize={16}
              fontWeight={500}
              color={"var(--neutral-500)"}
            >
              Disclosure
            </Typography>
          </Grid2>
          <Grid2
            container
            size={9}
            spacing={2}
            display={"flex"}
            alignItems={"center"}
          >
            <Controller
              control={form.control}
              name="disclosure"
              render={({ field }) => (
                <GKTextArea {...field} placeholder="Disclosure" />
              )}
            />
          </Grid2>
          <Grid2 size={12}>
            <Divider />
          </Grid2>
          <Grid2 size={3}>
            <Typography
              fontSize={16}
              fontWeight={500}
              color={"var(--neutral-500)"}
            >
              Investor Charter
            </Typography>
          </Grid2>
          <Grid2 container size={9} spacing={0}>
            <Controller
              control={form.control}
              name="charter"
              render={({ field }) => (
                <DragDrop
                  {...field}
                  uploadError={uploadError}
                  setUploadError={setUploadError}
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e);
                  }}
                  supportName=".png, .jpg, .pdf"
                  acceptType={".pdf,.png,.jpg"}
                />
              )}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <GKButton
                variant="link"
                target="__blank"
                href="https://www.sebi.gov.in/sebiweb/home/HomeAction.do?doListing=yes&sid=1&ssid=7&smid=0"
              >
                Click here
              </GKButton>
              <Typography fontSize={13} color="var(--grey-500)">
                to download your applicable Investor Charter.
              </Typography>
            </Box>
          </Grid2>
          <Grid2 size={12}>
            <Divider />
          </Grid2>

          <Grid2 size={3}>
            <Typography
              fontSize={16}
              fontWeight={500}
              color={"var(--neutral-500)"}
            >
              Policies
            </Typography>
          </Grid2>

          <Grid2 container size={9} spacing={2}>
            <Grid2
              size={12}
              sx={{
                "& .ck.ck-content.ck-editor__editable": {
                  maxHeight: "300px",
                  overflow: "auto",
                },
              }}
            >
              <Controller
                name="refundPolicy"
                control={form.control}
                render={({ field, fieldState: { error } }) => (
                  <GKEditor
                    {...field}
                    inputLabel="refund Policy"
                    error={!!error}
                    helperText={error?.message}
                    toolbar={[
                      "undo",
                      "redo",
                      "|",
                      "heading",
                      "bold",
                      "italic",
                      "underline",
                      "strikethrough",
                      "blockQuote",
                      "|",
                      "fontSize",
                      "fontColor",
                      "fontBackgroundColor",
                      "|",
                      "alignment",
                      "subscript",
                      "superscript",
                      "|",
                      "bulletedList",
                      "numberedList",
                    ]}
                  />
                )}
              />
            </Grid2>
            <Grid2
              size={12}
              sx={{
                "& .ck.ck-content.ck-editor__editable": {
                  maxHeight: "300px",
                  overflow: "auto",
                },
              }}
            >
              <Controller
                name="termsConditions"
                control={form.control}
                render={({ field, fieldState: { error } }) => (
                  <GKEditor
                    {...field}
                    inputLabel="terms & Conditions"
                    error={!!error}
                    helperText={error?.message}
                    toolbar={[
                      "undo",
                      "redo",
                      "|",
                      "heading",
                      "bold",
                      "italic",
                      "underline",
                      "strikethrough",
                      "blockQuote",
                      "|",
                      "fontSize",
                      "fontColor",
                      "fontBackgroundColor",
                      "|",
                      "alignment",
                      "subscript",
                      "superscript",
                      "|",
                      "bulletedList",
                      "numberedList",
                    ]}
                  />
                )}
              />
            </Grid2>
            <Grid2
              size={12}
              sx={{
                "& .ck.ck-content.ck-editor__editable": {
                  maxHeight: "300px",
                  overflow: "auto",
                },
              }}
            >
              <Controller
                name="pricingPolicy"
                control={form.control}
                render={({ field, fieldState: { error } }) => (
                  <GKEditor
                    {...field}
                    inputLabel="pricing Policy"
                    error={!!error}
                    helperText={error?.message}
                    toolbar={[
                      "undo",
                      "redo",
                      "|",
                      "heading",
                      "bold",
                      "italic",
                      "underline",
                      "strikethrough",
                      "blockQuote",
                      "|",
                      "fontSize",
                      "fontColor",
                      "fontBackgroundColor",
                      "|",
                      "alignment",
                      "subscript",
                      "superscript",
                      "|",
                      "bulletedList",
                      "numberedList",
                    ]}
                  />
                )}
              />
            </Grid2>
          </Grid2>

          <Grid2 size={12}>
            <Divider />
          </Grid2>
          <Grid2 size={3}>
            <Typography
              fontSize={16}
              fontWeight={500}
              color={"var(--neutral-500)"}
            >
              Compliance Audit Report{" "}
            </Typography>
          </Grid2>
          <Show.When isTrue={form.getValues("complianceAudit")?.length > 0}>
            <Grid2
              container
              size={9}
              spacing={2}
              display={"flex"}
              alignItems={"center"}
            >
              {form.getValues("complianceAudit").map((item, index) => {
                return (
                  <Grid2 size={12} container>
                    <Grid2 size={3}>
                      <Controller
                        control={form.control}
                        name={`complianceAudit.${index}.year`}
                        render={({ field }) => {
                          return (
                            <GKSearchSelect
                              inputLabel="Financial Year"
                              options={handleReturnNaturalRowsForString({
                                options: orderBy(
                                  financialYearFilter,
                                  "name",
                                  "desc",
                                ),
                                optionsValueKey: "name",
                                values: field.value || "",
                              })}
                              value={field.value}
                              onChange={(e, val) => {
                                field.onChange(val?.name);
                              }}
                              getOptionLabel={(option) => option.name || option}
                            />
                          );
                        }}
                      />
                    </Grid2>
                    <Grid2 size="auto" mt={3.5}>
                      <Typography color={grey[600]}>
                        Audit Report:{" "}
                        <span
                          style={{ fontWeight: 600, color: "var(--black)" }}
                        >
                          {
                            form.getValues(`complianceAudit.${index}.report1`)
                              ?.length
                          }
                        </span>
                      </Typography>
                    </Grid2>
                    <Grid2 size="auto" mt={3.5} mr={"auto"}>
                      <Typography color={grey[600]}>
                        Adverse Findings:{" "}
                        <span
                          style={{ fontWeight: 600, color: "var(--black)" }}
                        >
                          {
                            form.getValues(`complianceAudit.${index}.report2`)
                              ?.length
                          }
                        </span>
                      </Typography>
                    </Grid2>
                    <Grid2 size="auto" mt={2.5}>
                      <GKButton
                        variant="contained"
                        startIcon={<AttachmentsIcon />}
                        onClick={() => {
                          setShowFilesModal(true);
                          setAttachmentsIndex(index);
                        }}
                      >
                        Upload Files
                      </GKButton>
                    </Grid2>
                    <Show.When
                      isTrue={form.getValues("complianceAudit").length !== 1}
                    >
                      <Grid2 size="auto" mt={2.6}>
                        <IconButton
                          onClick={() => {
                            const newObj = form
                              .getValues("complianceAudit")
                              .filter((d, i) => i !== index);

                            form.setValue("complianceAudit", newObj);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid2>
                    </Show.When>
                  </Grid2>
                );
              })}
            </Grid2>
            <Grid2 size={3} />
          </Show.When>
          <Grid2 size={9}>
            <GKButton
              variant="outlined"
              size="small"
              startIcon={<PlusIcon />}
              onClick={() => {
                const newObj = [
                  ...form.getValues("complianceAudit"),
                  {
                    report1: [],
                    report2: [],
                    year: "",
                  },
                ];
                form.setValue("complianceAudit", newObj);
              }}
            >
              Add Financial Year
            </GKButton>
          </Grid2>
          <Grid2 size={12}>
            <Divider />
          </Grid2>
          <Grid2 size={3}>
            <Typography
              fontSize={16}
              fontWeight={500}
              color={"var(--neutral-500)"}
            >
              SEBI Info
            </Typography>
          </Grid2>

          <Grid2 container size={9} spacing={2}>
            <Grid2 size={6}>
              <Controller
                name="sebiRegisteredSupervisoryBody"
                control={form.control}
                render={({ field }) => (
                  <GKTextField
                    {...field}
                    inputLabel="SEBI Registered Supervisory Body"
                  />
                )}
              />
            </Grid2>
            <Grid2 size={6}>
              <Controller
                name="supervisoryBodyMembershipId"
                control={form.control}
                render={({ field }) => (
                  <GKTextField
                    {...field}
                    inputLabel="Supervisory Body Membership Id"
                  />
                )}
              />
            </Grid2>
          </Grid2>

          <Grid2 size={12} my={1}>
            <Divider />
          </Grid2>

          <Grid2 size={3}>
            <Typography
              fontSize={16}
              fontWeight={500}
              color={"var(--neutral-500)"}
            >
              Compliance Officer Details
            </Typography>
          </Grid2>

          <Grid2 container size={9} spacing={2}>
            <Grid2 size={4}>
              <Controller
                name="complianceOfficerName"
                control={form.control}
                render={({ field }) => (
                  <GKTextField {...field} inputLabel="Officer Name" />
                )}
              />
            </Grid2>
            <Grid2 size={4}>
              <Controller
                name="complianceRedressalEmail"
                control={form.control}
                render={({ field }) => (
                  <GKTextField {...field} inputLabel="Redressal Email" />
                )}
              />
            </Grid2>
            <Grid2 size={4}>
              <Controller
                name="compliancePhoneNumber"
                control={form.control}
                rules={{
                  required: {
                    value: true,
                    message: "Please enter phone number",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <PhoneInput
                    {...field}
                    onSelectChange={(event: any) => {
                      form.setValue("complianceCountryCode", event);
                    }}
                    onTextChange={(event: any) => {
                      field.onChange(event);
                    }}
                    inputLabel="Phone Number"
                    onTextValue={field.value}
                    onSelectValue={form.getValues("complianceCountryCode")}
                    error={!!error || hasError}
                    helperText={error?.message}
                    hasError={hasError}
                    setHasError={setHasError}
                  />
                )}
              />
            </Grid2>
          </Grid2>

          <Grid2 size={12} my={1}>
            <Divider />
          </Grid2>

          <Grid2 size={3}>
            <Typography
              fontSize={16}
              fontWeight={500}
              color={"var(--neutral-500)"}
            >
              Grievance Officer Details
            </Typography>
          </Grid2>

          <Grid2 container size={9} spacing={2}>
            <Grid2 size={4}>
              <Controller
                name="grievanceOfficerName"
                control={form.control}
                render={({ field }) => (
                  <GKTextField {...field} inputLabel="Officer Name" />
                )}
              />
            </Grid2>
            <Grid2 size={4}>
              <Controller
                name="grievanceRedressalEmail"
                control={form.control}
                render={({ field }) => (
                  <GKTextField {...field} inputLabel=" Redressal Email" />
                )}
              />
            </Grid2>
            <Grid2 size={4}>
              <Controller
                name="grievancePhoneNumber"
                control={form.control}
                rules={{
                  required: {
                    value: true,
                    message: "Please enter phone number",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <PhoneInput
                    {...field}
                    inputLabel="Phone Number"
                    onSelectChange={(event: any) => {
                      form.setValue("grievanceCountryCode", event);
                    }}
                    onTextChange={(event: any) => {
                      field.onChange(event);
                    }}
                    onTextValue={field.value}
                    onSelectValue={form.getValues("grievanceCountryCode")}
                    error={!!error || hasError}
                    helperText={error?.message}
                    hasError={hasError}
                    setHasError={setHasError}
                  />
                )}
              />
            </Grid2>
          </Grid2>
          <Grid2 size={12}>
            <Divider />
          </Grid2>

          <Grid2 size={12} textAlign={"right"}>
            <GKButton type="submit" variant="contained">
              Save
            </GKButton>
          </Grid2>
        </Grid2>
        <Show.When isTrue={showFilesModal}>
          <AuditFilesUpload
            setOpen={setShowFilesModal}
            open={showFilesModal}
            index={attachmentsIndex}
          />
        </Show.When>
      </form>
    </FormProvider>
  );
};

export default ComplianceSettings;
